import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Container,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../../utils/apiClient";
import { SubmitFeedbackRequest } from "@services/feedback/types";

interface FeedbackContext {
  messageId: string;
  destinationEmail: string;
  dobbyActionId?: string;
}

interface SubmitFeedbackResponse {
  success: boolean;
  feedbackId?: string;
  error?: string;
}

export function FeedbackFormPage() {
  const [searchParams] = useSearchParams();
  const [feedbackContext, setFeedbackContext] =
    useState<FeedbackContext | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  const extra = searchParams.get("extra");

  useEffect(() => {
    if (!extra) {
      setContextError(
        "Missing feedback context. This page should be accessed from a Dobby email."
      );
      return;
    }

    try {
      const decodedContext = JSON.parse(
        decodeURIComponent(extra)
      ) as FeedbackContext;

      // if (!decodedContext.messageId || !decodedContext.destinationEmail) {
      //   setContextError("Invalid feedback context. Missing required information.");
      //   return;
      // }

      setFeedbackContext(decodedContext);
      console.log("Feedback context loaded:", decodedContext);
    } catch (parseError) {
      setContextError(
        "Invalid feedback context format. Please access this page from a Dobby email."
      );
    }
  }, [searchParams, extra]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedbackContext) {
      setError("Missing feedback context");
      return;
    }

    if (!feedbackText.trim()) {
      setError("Please enter your feedback");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const request: SubmitFeedbackRequest = {
        feedbackText: feedbackText.trim(),
        extra: extra,
        messageId: feedbackContext.messageId,
        destinationEmail: feedbackContext.destinationEmail,
        ...(feedbackContext.dobbyActionId && {
          dobbyActionId: feedbackContext.dobbyActionId,
        }),
      };

      console.log("Submitting feedback:", request);

      const response = await apiClient.post<
        SubmitFeedbackResponse,
        SubmitFeedbackRequest
      >("/feedback/submit", request);

      if (response.success) {
        setIsSubmitted(true);
        setFeedbackText("");
        console.log("Feedback submitted successfully:", response.feedbackId);
      } else {
        setError(response.error || "Failed to submit feedback");
      }
    } catch (submitError) {
      console.error("Error submitting feedback:", submitError);
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to submit feedback"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedbackText(e.target.value);
    if (error) setError(null); // Clear error when user starts typing
  };

  if (contextError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" color="error" gutterBottom>
            Invalid Access
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {contextError}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!feedbackContext) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading feedback form...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4" sx={{ mb: 2, color: "#0088D1" }}>
            Dobby 🪄
          </Typography>
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you for your feedback! We'll use this to improve our email
            routing.
          </Alert>
          <Typography variant="body1" color="text.secondary">
            Your feedback has been recorded and will help us make Dobby better.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 1, color: "#0088D1" }}>
            Dobby 🪄
          </Typography>
          <Typography variant="h5" gutterBottom>
            Something went wrong?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Help us improve by telling us what happened.
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="What went wrong? *"
            placeholder="Please describe the issue with the email routing..."
            value={feedbackText}
            onChange={handleTextChange}
            required
            inputProps={{ maxLength: 2000 }}
            helperText={`${feedbackText.length}/2000 characters`}
            sx={{ mb: 3 }}
            error={!!error}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting || !feedbackText.trim()}
            sx={{
              backgroundColor: "#0088D1",
              "&:hover": {
                backgroundColor: "#0077B8",
              },
              py: 1.5,
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              "Send Feedback"
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
